import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CalendarRange,
  CalendarX,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  RefreshCw,
  Search,
  UserX,
  X,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../config/attendanceBranches";
import { getVisibleAttendanceBranches } from "../config/appBranches";
import {
  ymd,
  monthTitle,
  parseFlexibleDateString,
  absentDatesUpToCutoff,
  cutoffDateForSelectedMonth,
  isSunday,
  loadMonthlyAttendanceRows,
  formatPresentDayUnits,
  fetchPunchDocsForEmployeeOnDay,
  setPayrollHalfDayMark,
  deletePayrollHalfDayMark,
  payrollHalfDayMarkExists,
} from "../helpers/payrollAttendanceCore";

export default function PayrollPage() {
  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const visibleBranches = useMemo(() => getVisibleAttendanceBranches(storedUser), [storedUser]);

  const [selectedMonth, setSelectedMonth] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [selectedShopLabel, setSelectedShopLabel] = useState(ATTENDANCE_BRANCHES[0].label);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [monthlyData, setMonthlyData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [diag, setDiag] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [detailDayYmd, setDetailDayYmd] = useState(null);
  const [detailDayKind, setDetailDayKind] = useState(null);
  const [dayPunchRows, setDayPunchRows] = useState([]);
  const [dayPunchLoading, setDayPunchLoading] = useState(false);
  const [dayPunchError, setDayPunchError] = useState("");
  const [dayHalfMarkExists, setDayHalfMarkExists] = useState(false);
  const [markSaving, setMarkSaving] = useState(false);

  const isCurrentMonth = useMemo(() => {
    const now = new Date();
    return (
      now.getFullYear() === selectedMonth.getFullYear() &&
      now.getMonth() === selectedMonth.getMonth()
    );
  }, [selectedMonth]);

  const currentShop = useMemo(() => {
    const pool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;
    return pool.find((b) => b.label === selectedShopLabel) || pool[0];
  }, [selectedShopLabel, visibleBranches]);

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!visibleBranches.length) return;
    const allowed = new Set(visibleBranches.map((b) => b.label));
    if (!allowed.has(selectedShopLabel)) {
      setSelectedShopLabel(visibleBranches[0].label);
    }
  }, [visibleBranches, selectedShopLabel]);

  /** Align with Flutter: optional `?shop=Spareway` or `?bossBranch=Main|Branch 2|Branch 3` (only if branch is allowed). */
  useEffect(() => {
    const pool = visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES;
    const shop = searchParams.get("shop");
    if (shop && pool.some((b) => b.label === shop)) {
      setSelectedShopLabel(shop);
      return;
    }
    const bossBranch = searchParams.get("bossBranch");
    const map = { Main: "Main Shop", "Branch 2": "Spareway", "Branch 3": "Branch 3" };
    if (bossBranch && map[bossBranch] && pool.some((b) => b.label === map[bossBranch])) {
      setSelectedShopLabel(map[bossBranch]);
    }
  }, [searchParams, visibleBranches]);

  const cutoffDate = useMemo(() => cutoffDateForSelectedMonth(selectedMonth), [selectedMonth]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 280);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!debouncedSearch) {
      setFiltered(monthlyData);
      return;
    }
    setFiltered(
      monthlyData.filter(
        (r) =>
          r.employeeName.toLowerCase().includes(debouncedSearch) ||
          r.employeeId.toLowerCase().includes(debouncedSearch)
      )
    );
  }, [debouncedSearch, monthlyData]);

  useEffect(() => {
    if (!selectedEmployee) return;
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedEmployee(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedEmployee]);

  useEffect(() => {
    if (!selectedEmployee) {
      setDetailDayYmd(null);
      setDetailDayKind(null);
      setDayPunchRows([]);
      setDayPunchError("");
      setDayPunchLoading(false);
      setDayHalfMarkExists(false);
    }
  }, [selectedEmployee]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedEmployee?.employeeId || !detailDayYmd) {
      setDayHalfMarkExists(false);
      return undefined;
    }
    (async () => {
      try {
        const ex = await payrollHalfDayMarkExists(currentShop, selectedEmployee.employeeId, detailDayYmd);
        if (!cancelled) setDayHalfMarkExists(ex);
      } catch {
        if (!cancelled) setDayHalfMarkExists(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEmployee, detailDayYmd, currentShop]);

  const loadDayPunches = useCallback(
    async (ymd, kind) => {
      if (!selectedEmployee || !ymd) return;
      setDetailDayYmd(ymd);
      setDetailDayKind(kind);
      setDayPunchLoading(true);
      setDayPunchError("");
      setDayPunchRows([]);
      try {
        const { rows } = await fetchPunchDocsForEmployeeOnDay(currentShop, selectedEmployee.employeeId, ymd);
        setDayPunchRows(rows);
      } catch (e) {
        setDayPunchError(e?.message || "Failed to load punches");
      } finally {
        setDayPunchLoading(false);
      }
    },
    [currentShop, selectedEmployee]
  );

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const { rows: finalRows, diagRows } = await loadMonthlyAttendanceRows(currentShop, selectedMonth);
      setMonthlyData(finalRows);
      setFiltered(
        debouncedSearch
          ? finalRows.filter(
              (r) =>
                r.employeeName.toLowerCase().includes(debouncedSearch) ||
                r.employeeId.toLowerCase().includes(debouncedSearch)
            )
          : finalRows
      );
      setDiag(diagRows);
      setSelectedEmployee((prev) => {
        if (!prev) return prev;
        const next = finalRows.find((r) => r.employeeId === prev.employeeId);
        return next || prev;
      });
    } catch (e) {
      setError(e?.message || "Failed to generate payroll attendance report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedShopLabel]);

  const absentToday = useMemo(() => {
    if (!isCurrentMonth) return [];
    const today = new Date();
    if (isSunday(today)) return [];
    const todayKey = ymd(today);
    return monthlyData
      .filter((r) => {
        if (r.absentDates.includes(todayKey)) return true;
        for (const s of r.absentDates) {
          const dt = parseFlexibleDateString(s);
          if (!dt) continue;
          if (
            dt.getFullYear() === today.getFullYear() &&
            dt.getMonth() === today.getMonth() &&
            dt.getDate() === today.getDate()
          ) {
            return true;
          }
        }
        return false;
      })
      .sort((a, b) => a.employeeName.localeCompare(b.employeeName));
  }, [isCurrentMonth, monthlyData]);

  /** Small donut: today working vs absent (no punch on a working day — not paid leave). */
  const todayWorkingChartData = useMemo(() => {
    if (!isCurrentMonth) return [];
    const today = new Date();
    if (isSunday(today)) return [];
    const n = monthlyData.length;
    const absent = absentToday.length;
    const working = Math.max(0, n - absent);
    if (n === 0) return [];
    return [
      { name: "Working", value: working, fill: "#059669" },
      { name: "Absent", value: absent, fill: "#ea580c" },
    ];
  }, [isCurrentMonth, monthlyData.length, absentToday.length]);

  const detailAbsentDates = useMemo(() => {
    if (!selectedEmployee) return [];
    return absentDatesUpToCutoff(selectedEmployee.absentDates, cutoffDate);
  }, [selectedEmployee, cutoffDate]);

  const goPrevMonth = () => {
    setSelectedMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  };

  const canGoNext = useMemo(() => {
    const now = new Date();
    const next = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1);
    const nowMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return next <= nowMonth;
  }, [selectedMonth]);

  const goNextMonth = () => {
    if (!canGoNext) return;
    setSelectedMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  };

  const goThisMonth = () => {
    const n = new Date();
    setSelectedMonth(new Date(n.getFullYear(), n.getMonth(), 1));
  };

  const isSelectedCurrentMonth = useMemo(() => {
    const n = new Date();
    return (
      selectedMonth.getFullYear() === n.getFullYear() && selectedMonth.getMonth() === n.getMonth()
    );
  }, [selectedMonth]);

  const monthHeading = useMemo(() => monthTitle(selectedMonth), [selectedMonth]);
  const dayLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }, []);
  const details = selectedEmployee;

  const saveHalfDayMark = async () => {
    if (!selectedEmployee?.employeeId || !detailDayYmd || markSaving) return;
    setMarkSaving(true);
    try {
      await setPayrollHalfDayMark(currentShop, selectedEmployee.employeeId, detailDayYmd);
      setDayHalfMarkExists(true);
      toast.success("Half-day mark saved. Totals updated.");
      await loadData();
    } catch (e) {
      toast.error(e?.message || "Could not save half-day mark");
    } finally {
      setMarkSaving(false);
    }
  };

  const removeHalfDayMark = async () => {
    if (!selectedEmployee?.employeeId || !detailDayYmd || markSaving) return;
    setMarkSaving(true);
    try {
      await deletePayrollHalfDayMark(currentShop, selectedEmployee.employeeId, detailDayYmd);
      setDayHalfMarkExists(false);
      toast.success("Half-day mark removed.");
      await loadData();
    } catch (e) {
      toast.error(e?.message || "Could not remove mark");
    } finally {
      setMarkSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="w-full px-3 pb-28 pt-2 sm:pt-4 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
          <div className="bg-gradient-to-br from-indigo-700 via-indigo-700 to-violet-900 px-4 py-4 text-white sm:px-5 sm:py-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-white/15 p-2 shrink-0">
                  <CalendarRange className="w-6 h-6 text-indigo-100" aria-hidden />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-indigo-200/90">Payroll</p>
                  <h2 className="text-lg font-bold tracking-tight sm:text-xl">Monitor</h2>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-[10px] font-semibold uppercase text-indigo-200/90 shrink-0">Branch</label>
                <select
                  value={selectedShopLabel}
                  onChange={(e) => setSelectedShopLabel(e.target.value)}
                  className="rounded-xl border border-white/25 bg-white px-3 py-2 text-xs font-semibold text-slate-900 shadow-sm min-w-[140px]"
                >
                  {(visibleBranches.length ? visibleBranches : ATTENDANCE_BRANCHES).map((b) => (
                    <option key={b.label} value={b.label}>
                      {b.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={loadData}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-wide hover:bg-white/25 disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden />
                  Refresh
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-3 rounded-xl bg-white p-3 text-slate-900 shadow-inner sm:flex-row sm:items-center sm:flex-wrap">
              <div className="flex items-center gap-1 justify-center sm:justify-start flex-1 min-w-[200px]">
                <button
                  type="button"
                  onClick={goPrevMonth}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="min-w-[10rem] flex-1 text-center">
                  <div className="text-sm font-bold text-slate-900">{monthHeading}</div>
                  <div className="text-[10px] text-slate-500">Cutoff {ymd(cutoffDate)}</div>
                </div>
                <button
                  type="button"
                  onClick={goNextMonth}
                  disabled={!canGoNext}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40"
                  aria-label="Next month"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={goThisMonth}
                  disabled={isSelectedCurrentMonth}
                  className="ml-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-[10px] font-bold uppercase text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                >
                  This month
                </button>
              </div>
              <div className="relative flex-1 min-w-[12rem]">
                <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or employee ID…"
                  className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-9 text-sm placeholder:text-slate-400 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
                {search.trim() ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="space-y-4 p-4 sm:p-5">
            {error && (
              <div
                className="flex gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                role="alert"
              >
                <AlertCircle className="w-5 h-5 shrink-0 text-red-600" aria-hidden />
                <span>{error}</span>
              </div>
            )}

            {isCurrentMonth && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                <div className="min-w-0 flex-1 rounded-lg border border-orange-200/80 bg-gradient-to-r from-orange-50 to-amber-50/80 p-2 sm:max-w-md">
                  <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                    <UserX className="h-3 w-3 text-orange-700 shrink-0" aria-hidden />
                    <span className="text-[11px] font-bold uppercase tracking-wide text-orange-950">Absent today</span>
                    <span className="text-[10px] text-orange-800/85">({dayLabel})</span>
                    <span className="ml-auto inline-flex items-center rounded-full bg-orange-200/90 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-orange-950">
                      {absentToday.length}
                    </span>
                  </div>
                  {absentToday.length === 0 ? (
                    <div className="text-[10px] leading-snug text-orange-900/85">
                      {isSunday(new Date())
                        ? "Sunday — no working-day check."
                        : "No absences recorded for today."}
                    </div>
                  ) : (
                    <div className="flex max-h-[4.5rem] flex-wrap gap-1 overflow-y-auto">
                      {absentToday.map((r) => (
                        <button
                          type="button"
                          key={r.employeeId}
                          className="rounded border border-orange-200/80 bg-white/90 px-1.5 py-0.5 text-left text-[10px] font-semibold text-orange-950 shadow-sm transition hover:border-orange-300 hover:bg-white"
                          onClick={() => setSelectedEmployee(r)}
                        >
                          {r.employeeName}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 flex-col items-center justify-center rounded-lg border border-slate-200 bg-white p-1.5 sm:w-[9.5rem]">
                  <p className="mb-0.5 text-[9px] font-bold uppercase tracking-wide text-slate-500">Today</p>
                  <div className="h-[5.5rem] w-full max-w-[7rem]">
                    {isSunday(new Date()) ? (
                      <div className="flex h-full items-center justify-center px-1 text-center text-[9px] text-slate-500">Sunday</div>
                    ) : todayWorkingChartData.length === 0 ? (
                      <div className="flex h-full items-center justify-center text-center text-[9px] text-slate-400">No roster</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Tooltip
                            formatter={(value, name) => [`${value}`, name]}
                            contentStyle={{ fontSize: 11, padding: "4px 8px" }}
                          />
                          <Pie
                            data={todayWorkingChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={14}
                            outerRadius={32}
                            paddingAngle={2}
                          >
                            {todayWorkingChartData.map((entry, i) => (
                              <Cell key={`cell-${i}`} fill={entry.fill} stroke="#fff" strokeWidth={1} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                  {!isSunday(new Date()) && monthlyData.length > 0 ? (
                    <div className="mt-0.5 flex w-full justify-center gap-2 text-[9px] tabular-nums text-slate-600">
                      <span className="text-emerald-700">W {Math.max(0, monthlyData.length - absentToday.length)}</span>
                      <span className="text-orange-700" title="Absent (no punch)">
                        Ab {absentToday.length}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-xl border border-slate-200/90 shadow-sm">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 z-[1] bg-slate-100/95 backdrop-blur-sm">
                    <tr>
                      <th className="border-b border-slate-200 p-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                        Employee
                      </th>
                      <th className="border-b border-slate-200 p-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600">
                        ID
                      </th>
                      <th className="border-b border-slate-200 p-3 text-right text-xs font-bold uppercase tracking-wide text-violet-900">
                        Years
                      </th>
                      <th className="border-b border-slate-200 p-3 text-right text-xs font-bold uppercase tracking-wide text-emerald-800">
                        Present
                      </th>
                      <th className="border-b border-slate-200 p-3 text-right text-xs font-bold uppercase tracking-wide text-orange-900">
                        Absent
                      </th>
                      <th className="border-b border-slate-200 p-3 text-right text-xs font-bold uppercase tracking-wide text-indigo-900">
                        Half
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <tr key={`sk-${i}`} className="animate-pulse">
                          <td className="border-b border-slate-100 p-3">
                            <div className="h-4 w-32 rounded bg-slate-200" />
                          </td>
                          <td className="border-b border-slate-100 p-3">
                            <div className="h-4 w-16 rounded bg-slate-200" />
                          </td>
                          <td className="border-b border-slate-100 p-3 text-right">
                            <div className="ml-auto h-4 w-8 rounded bg-violet-100" />
                          </td>
                          <td className="border-b border-slate-100 p-3 text-right">
                            <div className="ml-auto h-4 w-8 rounded bg-emerald-100" />
                          </td>
                          <td className="border-b border-slate-100 p-3 text-right">
                            <div className="ml-auto h-4 w-8 rounded bg-orange-100" />
                          </td>
                          <td className="border-b border-slate-100 p-3 text-right">
                            <div className="ml-auto h-4 w-8 rounded bg-indigo-100" />
                          </td>
                        </tr>
                      ))
                    ) : filtered.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-10 text-center">
                          <div className="mx-auto max-w-sm text-sm text-slate-600">
                            {search.trim() ? (
                              <>
                                <p className="font-semibold text-slate-800">No matching employees</p>
                                <p className="mt-1 text-slate-500">Try a different name or ID.</p>
                              </>
                            ) : monthlyData.length === 0 ? (
                              <>
                                <p className="font-semibold text-slate-800">No attendance loaded</p>
                                <p className="mt-1 text-slate-500">
                                  Refresh after employees and punch data are available for this branch and month.
                                </p>
                              </>
                            ) : (
                              <p className="text-slate-500">No rows to show.</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r) => (
                        <tr
                          key={r.employeeId}
                          className="cursor-pointer border-b border-slate-100 odd:bg-white even:bg-slate-50/70 transition-colors hover:bg-indigo-50/60"
                          onClick={() => setSelectedEmployee(r)}
                        >
                          <td className="p-3 font-semibold text-slate-900">{r.employeeName}</td>
                          <td className="p-3 font-mono text-xs text-slate-600">{r.employeeId}</td>
                          <td className="p-3 text-right font-bold tabular-nums text-violet-700">
                            {r.tenureLabel || "—"}
                          </td>
                          <td className="p-3 text-right font-bold tabular-nums text-emerald-700">
                            {formatPresentDayUnits(r.presentDays)}
                          </td>
                          <td className="p-3 text-right font-bold tabular-nums text-orange-700">
                            {absentDatesUpToCutoff(r.absentDates, cutoffDate).length}
                          </td>
                          <td className="p-3 text-right font-bold tabular-nums text-indigo-700">
                            {r.halfDayDates.length}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {diag.length > 0 && (
              <details className="rounded-xl border border-slate-200 bg-slate-50/80">
                <summary className="cursor-pointer px-3 py-2.5 text-xs font-semibold text-slate-600 hover:text-slate-900">
                  Collection diagnostics
                </summary>
                <div className="overflow-auto border-t border-gray-200">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-100/80">
                      <tr>
                        <th className="border-b border-gray-200 p-2 text-left">Collection</th>
                        <th className="border-b border-gray-200 p-2 text-left">Type</th>
                        <th className="border-b border-gray-200 p-2 text-right">Scanned</th>
                        <th className="border-b border-gray-200 p-2 text-right">Used</th>
                        <th className="border-b border-gray-200 p-2 text-left">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diag.map((d, i) => (
                        <tr key={`${d.collection}-${i}`} className="odd:bg-white even:bg-gray-50/80">
                          <td className="border-b border-gray-100 p-2">{d.collection}</td>
                          <td className="border-b border-gray-100 p-2">{d.type}</td>
                          <td className="border-b border-gray-100 p-2 text-right">{d.scanned}</td>
                          <td className="border-b border-gray-100 p-2 text-right">{d.used}</td>
                          <td className="border-b border-gray-100 p-2 text-red-600">{d.error || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        </div>
      </main>

      {details && (
        <div
          className="fixed inset-0 z-[1200] flex items-end justify-center bg-slate-900/50 p-2 backdrop-blur-[2px] md:items-center"
          onClick={() => setSelectedEmployee(null)}
          role="presentation"
        >
          <div
            className="max-h-[85vh] w-full max-w-5xl overflow-auto rounded-t-2xl border border-slate-200 bg-white p-4 shadow-2xl md:rounded-2xl md:p-6"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="payroll-detail-title"
          >
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-violet-700 text-sm font-bold text-white">
                {(details.employeeName || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h2 id="payroll-detail-title" className="text-lg font-bold text-slate-900">
                  {details.employeeName}{" "}
                  <span className="font-mono text-sm font-semibold text-slate-500">({details.employeeId})</span>
                </h2>
                <p className="mt-0.5 text-xs text-slate-500">
                  {details.month} · {selectedShopLabel} · cutoff {ymd(cutoffDate)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEmployee(null)}
                className="rounded-lg border border-slate-200 p-2 text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4 flex flex-wrap gap-2 text-sm">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 font-semibold text-emerald-900">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                Present {formatPresentDayUnits(details.presentDays)}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 font-semibold text-orange-950">
                <CalendarX className="h-3.5 w-3.5" aria-hidden />
                Absent {detailAbsentDates.length}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 font-semibold text-indigo-900">
                <Clock className="h-3.5 w-3.5" aria-hidden />
                Half {details.halfDayDates.length}
              </span>
            </div>

            <p className="mb-3 text-center text-[11px] text-slate-500">
              Select a date below to load punch records for that day from the attendance collection.
            </p>
            <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
              <div>
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                  Present dates
                </h3>
                <div className="h-56 overflow-auto rounded-xl border border-emerald-100 bg-emerald-50/30 p-2">
                  {details.presentDates.length === 0 ? (
                    <div className="text-slate-500">None</div>
                  ) : (
                    details.presentDates.map((d) => (
                      <button
                        type="button"
                        key={d}
                        onClick={() => loadDayPunches(d, "present")}
                        className={`flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-emerald-900 transition hover:bg-emerald-100/80 ${
                          detailDayYmd === d && detailDayKind === "present"
                            ? "bg-white ring-2 ring-emerald-500 ring-offset-1"
                            : ""
                        }`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
                        <span className="font-mono text-xs">{d}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div>
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-orange-950">
                  <CalendarX className="h-4 w-4" aria-hidden />
                  Absent dates
                </h3>
                <div className="h-56 overflow-auto rounded-xl border border-orange-100 bg-orange-50/30 p-2">
                  {detailAbsentDates.length === 0 ? (
                    <div className="text-slate-500">None</div>
                  ) : (
                    detailAbsentDates.map((d) => (
                      <button
                        type="button"
                        key={d}
                        onClick={() => loadDayPunches(d, "absent")}
                        className={`flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-orange-950 transition hover:bg-orange-100/80 ${
                          detailDayYmd === d && detailDayKind === "absent"
                            ? "bg-white ring-2 ring-orange-500 ring-offset-1"
                            : ""
                        }`}
                      >
                        <CalendarX className="h-3.5 w-3.5 shrink-0 text-orange-600" aria-hidden />
                        <span className="font-mono text-xs">{d}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div>
                <h3 className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-indigo-900">
                  <Clock className="h-4 w-4" aria-hidden />
                  Half-day dates
                </h3>
                <div className="h-56 overflow-auto rounded-xl border border-indigo-100 bg-indigo-50/30 p-2">
                  {details.halfDayDates.length === 0 ? (
                    <div className="text-slate-500">None</div>
                  ) : (
                    details.halfDayDates.map((d) => (
                      <button
                        type="button"
                        key={d}
                        onClick={() => loadDayPunches(d, "half")}
                        className={`flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-indigo-900 transition hover:bg-indigo-100/80 ${
                          detailDayYmd === d && detailDayKind === "half"
                            ? "bg-white ring-2 ring-indigo-500 ring-offset-1"
                            : ""
                        }`}
                      >
                        <Clock className="h-3.5 w-3.5 shrink-0 text-indigo-600" aria-hidden />
                        <span className="font-mono text-xs">{d}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>

            {(detailDayYmd || dayPunchLoading) && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900">
                      Punches on <span className="font-mono text-indigo-700">{detailDayYmd || "…"}</span>
                    </h3>
                    {dayHalfMarkExists ? (
                      <p className="mt-1 text-[11px] font-semibold text-indigo-800">
                        Manual half-day mark is on for this date (adds ½ day in totals).
                      </p>
                    ) : (
                      <p className="mt-1 text-[11px] text-slate-600">
                        Use <strong className="text-slate-800">Mark half day</strong> to add ½ day when there is no punch
                        or payroll needs a correction (saved in Firestore).
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={saveHalfDayMark}
                      disabled={markSaving || !detailDayYmd || dayHalfMarkExists}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white shadow hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {markSaving ? "Saving…" : "Mark half day"}
                    </button>
                    <button
                      type="button"
                      onClick={removeHalfDayMark}
                      disabled={markSaving || !detailDayYmd || !dayHalfMarkExists}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Remove mark
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDetailDayYmd(null);
                        setDetailDayKind(null);
                        setDayPunchRows([]);
                        setDayPunchError("");
                        setDayHalfMarkExists(false);
                      }}
                      className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      Clear day
                    </button>
                  </div>
                </div>
                {dayPunchLoading ? (
                  <p className="text-sm text-slate-600">Loading punches…</p>
                ) : dayPunchError ? (
                  <p className="text-sm text-red-700">{dayPunchError}</p>
                ) : dayPunchRows.length === 0 ? (
                  <p className="text-sm text-slate-600">
                    No punch documents matched this employee for this calendar day. Absent days often have no punches.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {dayPunchRows.map((row) => (
                      <li
                        key={row.id}
                        className="rounded-lg border border-slate-200 bg-white p-3 text-xs shadow-sm"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
                          <span className="font-mono font-semibold text-slate-800">Doc {row.id}</span>
                          <span className="tabular-nums text-slate-600">
                            Day weight <strong>{row.weight}</strong>
                            {row.halfFlag ? (
                              <span className="ml-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-indigo-800">
                                Half flag
                              </span>
                            ) : null}
                          </span>
                        </div>
                        {row.times.length > 0 ? (
                          <ul className="mt-2 space-y-1 font-mono text-[11px] text-slate-800">
                            {row.times.map((t, i) => (
                              <li key={`${row.id}-t-${i}`}>{t}</li>
                            ))}
                          </ul>
                        ) : (
                          <p className="mt-2 text-[11px] text-slate-500">No timestamp fields parsed (check raw fields in Firestore).</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-600">
                          {row.branch ? (
                            <span>
                              <span className="font-semibold text-slate-700">Branch</span> {row.branch}
                            </span>
                          ) : null}
                          {row.type ? (
                            <span>
                              <span className="font-semibold text-slate-700">Type</span> {row.type}
                            </span>
                          ) : null}
                          {row.status ? (
                            <span>
                              <span className="font-semibold text-slate-700">Status</span> {row.status}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <p className="mt-4 text-center text-[10px] text-slate-400">Press Esc to close</p>
          </div>
        </div>
      )}
    </div>
  );
}
